// Formatting for the Affiliate links pages. Kept free of React so the rules
// (money, rates, titles) are testable on their own.

import { legLabel } from '@/components/leg-search/legModel'
import { combinedDecimalOdds, formatDecimalOdds } from '@/utils/odds'

const DECIMAL_NUMBER_PATTERN = /^-?\d+(\.\d+)?$/
const LEADING_AT_SIGNS_PATTERN = /^@+/
const CURRENCY_AMOUNT_PATTERN = /^\d+(\.\d{1,2})?$/
const UNSIGNED_DECIMAL_PATTERN = /^\d+(\.\d+)?$/
const NONZERO_DECIMAL_DIGIT_PATTERN = /\.\d*[1-9]/

// Marketing links belong to a named affiliate; player links to the player who shared.
export const SOURCE_FILTERS = [
  { value: '', label: 'All links' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'user', label: 'Player links' },
]

export const STATUS_FILTERS = [
  { value: '', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
]

export const DEVICE_FILTERS = [
  { value: '', label: 'All devices' },
  { value: 'ios', label: 'iOS' },
  { value: 'android', label: 'Android' },
  { value: 'desktop', label: 'Desktop' },
  { value: 'other', label: 'Other' },
]

export const CHANNEL_FILTERS = [
  { value: '', label: 'All channels' },
  { value: 'link', label: 'Link (any)' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'x', label: 'X' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'email', label: 'Email' },
  { value: 'native', label: 'Share sheet' },
  { value: 'image', label: 'Image' },
  { value: 'direct', label: 'Link (no marker)' },
  { value: 'code', label: 'Booking code' },
]

// "Live" means in-play in a sportsbook, so link status never says it. An
// unmapped code shows as itself, so it can be diagnosed.
const STATUS_LABELS = { active: 'Active', expired: 'Expired', disabled: 'Ended' }
const STATUS_TONES = { active: 'success', expired: 'default', disabled: 'default' }
export const statusLabel = (status) => STATUS_LABELS[status] || status || '—'
export const statusTone = (status) => STATUS_TONES[status] || 'default'

const PLACEMENT_LABELS = { betslip: 'Betslip', receipt: 'Receipt', bet_history: 'My Bets', studio: 'Studio' }
export const placementLabel = (placement) => PLACEMENT_LABELS[placement] || placement || '—'
// Where a link came from, for the breakdown: marketing links are made in
// Studio, so that row says what it is.
export const placementSourceLabel = (placement) => (placement === 'studio' ? 'Studio (marketing links)' : placementLabel(placement))

const COUPON_LABELS = { single: 'Single', combination: 'Combination', system: 'System' }
export const couponLabel = (type) => COUPON_LABELS[type] || type || '—'

const DEVICE_LABELS = { ios: 'iOS', android: 'Android', desktop: 'Desktop', other: 'Other' }
export const deviceLabel = (value) => DEVICE_LABELS[value] || value || 'Other'

const CHANNEL_LABELS = {
  whatsapp: 'WhatsApp',
  x: 'X',
  facebook: 'Facebook',
  telegram: 'Telegram',
  email: 'Email',
  native: 'Share sheet',
  image: 'Image',
  direct: 'Link (no marker)',
  code: 'Booking code',
  copy_link: 'Copy link',
  copy_code: 'Copy code',
  link: 'Link',
}
export const channelLabel = (value) => CHANNEL_LABELS[value] || value || 'Other'

export function channelApiParams(value) {
  if (value === 'link') return { channel: 'link' }
  if (value === 'code') return { channel: 'code' }
  if (value) return { via: value }
  return {}
}

export const formatCount = (value) => (Number.isFinite(value) ? value : 0).toLocaleString('en')

const moneyFormats = new Map()

// How much of an amount a short slot (a tile, an axis) shows: small amounts
// keep their minor units, hundreds drop them, and ten thousand and up are
// abbreviated. Tables always write the amount out.
const COMPACT_STYLES = {
  whole: { minimumFractionDigits: 0, maximumFractionDigits: 0 },
  abbreviated: { notation: 'compact', maximumFractionDigits: 1 },
}

function moneyFormat(currency, style) {
  const key = `${currency}:${style}`
  if (!moneyFormats.has(key)) {
    try {
      // en-US writes a currency without a US symbol by its code (ZAR 12.50),
      // so a rand amount never reads as dollars, and every currency keeps
      // its own minor units (¥1,235, ZAR 12.50).
      moneyFormats.set(key, new Intl.NumberFormat('en-US', { style: 'currency', currency, ...COMPACT_STYLES[style] }))
    } catch {
      moneyFormats.set(key, null)
    }
  }
  return moneyFormats.get(key)
}

function amountValue(amount) {
  if (typeof amount === 'number') return amount
  const text = String(amount ?? '').trim()
  return DECIMAL_NUMBER_PATTERN.test(text) ? Number(text) : Number.NaN
}

/**
 * An amount in its own currency, with that currency's minor units:
 * "ZAR 1,234.50", "$9.69". `compact` is for a short slot: "ZAR 12.50",
 * "ZAR 1,235", "ZAR 12.3K".
 */
export function formatMoney(currency, amount, { compact = false } = {}) {
  const value = amountValue(amount)
  if (!Number.isFinite(value)) return `${currency} ${amount}`
  const size = Math.abs(value)
  const style = !compact || size < 100 ? 'standard' : size < 10000 ? 'whole' : 'abbreviated'
  const format = moneyFormat(currency, style)
  return format ? format.format(value) : `${currency} ${value.toFixed(2)}`
}

/** The currencies staked, largest total first; zero amounts are left out. */
export function stakeCurrencies(stakeByCurrency) {
  return Object.entries(stakeByCurrency || {})
    .map(([currency, amount]) => [currency, amountValue(amount)])
    .filter(([, value]) => Number.isFinite(value) && value !== 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([currency]) => currency)
}

/** Currencies that have stake, payout or GGR, ranked by stake first. */
export function moneyCurrencies(stats) {
  const currencies = new Set([
    ...Object.keys(stats?.stake_by_currency || {}),
    ...Object.keys(stats?.payout_by_currency || {}),
    ...Object.keys(stats?.ggr_by_currency || {}),
  ])
  return [...currencies]
    .filter((currency) => currency)
    .sort((a, b) => {
      const stakeDelta = stakeIn(stats?.stake_by_currency, b) - stakeIn(stats?.stake_by_currency, a)
      if (stakeDelta) return stakeDelta
      const ggrDelta = stakeIn(stats?.ggr_by_currency, b) - stakeIn(stats?.ggr_by_currency, a)
      return ggrDelta || a.localeCompare(b)
    })
}

/** The stake in one currency as a number; 0 when nothing was staked in it. */
export function stakeIn(stakeByCurrency, currency) {
  const value = amountValue(stakeByCurrency?.[currency])
  return Number.isFinite(value) ? value : 0
}

/**
 * A stake cell: the amount in the currency the page shows, and any other
 * currency the row was staked in, written out beside it. Money is never
 * added across currencies.
 */
export function stakeParts(stakeByCurrency, currency) {
  const staked = stakeCurrencies(stakeByCurrency)
  const shown = currency && staked.includes(currency) ? formatMoney(currency, stakeByCurrency[currency]) : '—'
  const others = staked.filter((code) => code !== currency).map((code) => formatMoney(code, stakeByCurrency[code]))
  return { shown, others }
}

export function moneyParts(moneyByCurrency, currency) {
  const currencies = Object.entries(moneyByCurrency || {})
    .map(([code, amount]) => [code, amountValue(amount)])
    .filter(([, value]) => Number.isFinite(value))
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([code]) => code)
  const shown = currency && currencies.includes(currency) ? formatMoney(currency, moneyByCurrency[currency]) : '—'
  const others = currencies.filter((code) => code !== currency).map((code) => formatMoney(code, moneyByCurrency[code]))
  return { shown, others }
}

/** Every currency staked, largest first; an em dash when nothing was. */
export function formatStake(stakeByCurrency) {
  const parts = stakeCurrencies(stakeByCurrency).map((currency) => formatMoney(currency, stakeByCurrency[currency]))
  return parts.length ? parts.join(' · ') : '—'
}

/** Leg labels, three at most; older shares without legs fall back to a count. */
export function linkTitle(link) {
  const legs = Array.isArray(link?.legs) ? link.legs : []
  if (legs.length) {
    const shown = legs.slice(0, 3).map((leg) => leg.label).join(' + ')
    return legs.length > 3 ? `${shown} + ${legs.length - 3} more` : shown
  }
  const count = link?.outcome_count || 0
  return `${count} ${count === 1 ? 'selection' : 'selections'}`
}

/** Bets per open as a ratio, or null when nothing was opened. */
export function conversion(bets, opens) {
  return opens ? (bets || 0) / opens : null
}

export function conversionRate(bets, opens) {
  const ratio = conversion(bets, opens)
  return ratio === null ? '—' : `${(ratio * 100).toFixed(1)}%`
}

/** The events a link's selections come from, each once, for its second line. */
export function linkEvents(link) {
  const legs = Array.isArray(link?.legs) ? link.legs : []
  return [...new Set(legs.map((leg) => String(leg.event || '').trim()).filter(Boolean))].join(' · ')
}

export function formatDate(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

// Affiliate handles are stored lower case without the "@".
export const HANDLE_PATTERN = /^[a-z0-9][a-z0-9_.-]{1,39}$/
export function normalizeHandle(value) {
  return String(value || '').trim().replace(LEADING_AT_SIGNS_PATTERN, '').toLowerCase()
}
export const formatHandle = (handle) => (handle ? `@${handle}` : '')

/** What the stake box accepts while typing: digits and one point, two decimals at most. */
export const STAKE_INPUT_PATTERN = /^\d{0,7}(\.\d{0,2})?$/
export const MAX_SUGGESTED_STAKE = 1000000
/** A suggested stake the API takes: positive, at most two decimals and 1,000,000. */
export function validSuggestedStake(value) {
  const text = String(value || '').trim()
  if (!CURRENCY_AMOUNT_PATTERN.test(text)) return false
  const amount = Number(text)
  return amount > 0 && amount <= MAX_SUGGESTED_STAKE
}
/** A suggested stake without a currency (it pre-fills the player's own). */
export function formatSuggestedStake(value) {
  if (!value || !UNSIGNED_DECIMAL_PATTERN.test(String(value))) return '—'
  const whole = !NONZERO_DECIMAL_DIGIT_PATTERN.test(String(value))
  return Number(value).toLocaleString('en', whole
    ? { maximumFractionDigits: 0 }
    : { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export const EXPIRY_OPTIONS = [
  { value: 1, label: '1 day' },
  { value: 3, label: '3 days' },
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
]
export const DEFAULT_EXPIRY_DAYS = 7

/**
 * The share leg snapshot for one outcome search result (a Prepacks leg): what
 * players see on the landing and in Live links.
 */
export function legFromOutcome(outcome) {
  return {
    outcome_id: String(outcome?.outcome_id ?? ''),
    event_id: outcome?.event_id ? String(outcome.event_id) : '',
    event: String(outcome?.event_label || '').trim(),
    label: legLabel(outcome),
    market: String(outcome?.market_label || '').trim(),
    odds: formatDecimalOdds(outcome?.decimal_odds),
  }
}

/** One sport when every leg agrees, "Multi-sport" otherwise. */
export function legsSport(outcomes) {
  const sports = [...new Set((outcomes || []).map((outcome) => String(outcome?.sport || '').trim()).filter(Boolean))]
  if (!sports.length) return ''
  return sports.length === 1 ? sports[0] : 'Multi-sport'
}

/** Combined decimal odds of a combination, two decimals; empty when a price is missing. */
export function combinedOdds(legs) {
  const total = combinedDecimalOdds(legs?.map((leg) => leg.odds))
  return total === null ? '' : total.toFixed(2)
}
